import { SelectionMenuViewMode } from '@components/ButtonBaseWithSelectionMenu/types/buttonBaseWithSelectionMenu.types';
import LangEnum from '../../../../../lang/LangEnum';
import { AddOrderProductTestIds } from '../../../../../utils/testIds/OrderProducts.testIds';
import { OfflineStatus } from '../../../../offline/domain/offlineOrder/offlineOrder.types';
import { AvailabilityEnum } from '../../../../products/ProductTypes';
import { OrderStatus } from '../../../interfaces/order-status.interface';
import { AddOrderProductMode } from '../../orderProducts.types';
import OrderProductDeliveryWindow from '../../product/components/OrderProductDeliveryWindow';
import OrderProductGridContainer from '../../product/grid/components/orderProductGrid/components/grid/components/OrderProductGridContainer';
import AddOrderProductRowView from './AddOrderProductRowView';
import { useAddOrderProductRow } from './hooks/useAddOrderProductRow';

interface AddOrderProductRowProps {
  orderId: string;
  orderStatus: OrderStatus | OfflineStatus;
  mode?: AddOrderProductMode;
}

const AddOrderProductRow = ({ orderId, orderStatus, mode }: AddOrderProductRowProps) => {
  const {
    inputProductValue,
    product,
    kit,
    isPreparingProduct,
    hasError,
    errorMessage,
    color,
    isLoadingColors,
    colorOptions,
    selectedColorOption,
    availability,
    deliveryWindow,
    grid,
    createOrderItems,
    totals,
    isSubmitting,
    codeInputRef,
    canAddWithoutDeliveryWindow,
    gridProduct,
    suggestedGrid,
    suggestedPacks,
    selectedPacks,
    hasValidPacks,
    showSuggestedGrid,
    suggestionAchieved,
    selloutSummary,
    getLimit,
    getStockValue,
    handleChangeCode,
    handleSubmitCode,
    handleBlurCode,
    handleSelectDeliveryWindow,
    handleChangeGridItem,
    handleSelectSuggestion,
    handleSelectPacks,
    handleAddProduct,
    resetSelectedPacks,
    onSelectColorOption,
  } = useAddOrderProductRow({ orderId, orderStatus, mode });

  const deliveryWindowSlot = (
    <OrderProductDeliveryWindow
      viewMode={SelectionMenuViewMode.EDIT}
      canEditOrder
      isFocusable
      isLoadingButton={false}
      disabled={!color || isPreparingProduct}
      deliveryWindow={isPreparingProduct ? undefined : deliveryWindow}
      availability={isPreparingProduct ? AvailabilityEnum.EMPTY : availability}
      productId={product?.id}
      productColorId={color?.id}
      label={LangEnum.GLOBAL_DELIVERY_LABEL}
      hasPlainEmptyState
      testId={AddOrderProductTestIds.DELIVERY_WINDOW}
      onSelectNewDeliveryWindow={handleSelectDeliveryWindow}
    />
  );

  const gridSlot =
    color && grid.length > 0 ? (
      <OrderProductGridContainer
        grid={grid}
        createOrderItems={createOrderItems}
        stackOrderItem={handleChangeGridItem}
        product={gridProduct}
        orderId={orderId}
        orderStatus={orderStatus}
        productSizeNames={grid.map((gridItem) => gridItem.productSizeName)}
        onClickEnter={handleAddProduct}
        getLimit={getLimit}
        getStockValue={getStockValue}
        suggestedGrid={suggestedGrid}
        suggestedPacks={suggestedPacks}
        selectedPacks={selectedPacks}
        hasValidPacks={hasValidPacks}
        showSuggestedGrid={showSuggestedGrid}
        suggestionAchieved={suggestionAchieved}
        selloutSummary={selloutSummary}
        handleSelectSuggestion={handleSelectSuggestion}
        handleSelectPacks={handleSelectPacks}
        resetSelectedPacks={resetSelectedPacks}
        handleClickPaste={() => Promise.resolve(undefined)}
        copiedGrid={{ grid: [], product: undefined }}
        removeCopiedGrid={() => undefined}
      />
    ) : null;

  return (
    <AddOrderProductRowView
      product={{
        inputProductValue,
        productName: product?.name ?? kit?.name,
        productCode: product?.code ?? kit?.code,
        isLoadingProduct: isPreparingProduct,
        hasError,
        errorMessage,
        codeInputRef,
        onChangeCode: handleChangeCode,
        onSubmitCode: handleSubmitCode,
        onBlurCode: handleBlurCode,
      }}
      color={{
        colors: colorOptions,
        selectedColor: isPreparingProduct ? null : selectedColorOption,
        isColorDisabled:
          isPreparingProduct || !product || isLoadingColors || colorOptions.length === 0,
        onSelectColor: onSelectColorOption,
      }}
      totals={{
        partsQuantity: totals.partsQuantity,
        total: totals.total,
      }}
      addButtonConfig={{
        show: !!kit || (!!product && (grid.length > 0 || canAddWithoutDeliveryWindow)),
        isSubmitting,
        onClick: handleAddProduct,
      }}
      deliveryWindowSlot={deliveryWindowSlot}
      gridSlot={gridSlot}
      grid={grid}
    />
  );
};

export default AddOrderProductRow;
